import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal, showToast } from '../../store/uiSlice';
import { addPendingOrder, updatePosition, setBalance } from '../../store/tradeSlice';
import { fmtP } from '../../utils/formatters';
import { INSTRUMENTS, LOT_LABELS, getLotSize } from '../../utils/constants';
import { getForexUSDPrice, getNotionalUSD } from '../../utils/forexUtils';
import api from '../../services/api';

const getSpread = (price, category) => {
  if (!price || price === 0) return 0;
  if (category === 'forex') return 0.0002;
  if (category === 'crypto') return price * 0.001;
  if (category === 'metal') return price * 0.002;
  return price * 0.002;
};

const getLeverageMultiplier = (leverageStr, category) => {
  if (category === 'crypto') return 25;
  if (category === 'stock') return 4;
  if (!leverageStr) return 50;
  const parts = String(leverageStr).split(':');
  return parseInt(parts[1]) || parseInt(parts[0]) || 50;
};

const getLeverageDisplay = (category, userLeverage) => {
  if (category === 'crypto') return '1:25';
  if (category === 'stock') return '1:4';
  return userLeverage || '1:100';
};

const getMarginRate = (category, userLeverage) => {
  const multiplier = getLeverageMultiplier(userLeverage, category);
  return (100 / multiplier).toFixed(1);
};

const getLotLabel = (cat, id) => {
  if (id === 'XAGUSD') return '1 lot = 1,000 oz';
  return LOT_LABELS[cat] || '1 lot = 1 unit';
};

const OrderModal = () => {
  const dispatch = useDispatch();
  const { orderModal } = useSelector(s => s.ui.modals);
  const { marketData, balance } = useSelector(s => s.trade);
  const user = useSelector(s => s.auth.user);

  const [tab, setTab] = useState('market');
  const [selectedId, setSelectedId] = useState(INSTRUMENTS[0]?.id || '');
  const [direction, setDirection] = useState('buy');
  const [pendingType, setPendingType] = useState('limit');
  const [quantity, setQuantity] = useState(0.01);
  const [qtyRaw, setQtyRaw] = useState('0.01');
  const [targetPrice, setTargetPrice] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const instrument = INSTRUMENTS.find(i => i.id === selectedId);
  const instrumentData = instrument ? marketData[instrument.id] : null;
  const dataReady = !!instrumentData?.price;

  const userLeverage = user?.leverage || '1:100';
  const leverageMultiplier = getLeverageMultiplier(userLeverage, instrument?.cat);
  const leverageDisplay = getLeverageDisplay(instrument?.cat, userLeverage);
  const marginRatePct = getMarginRate(instrument?.cat, userLeverage);

  useEffect(() => {
    if (orderModal) {
      setTab('market'); setSelectedId(INSTRUMENTS[0]?.id || ''); setDirection('buy');
      setPendingType('limit'); setQuantity(0.01); setQtyRaw('0.01');
      setTargetPrice(''); setTakeProfit(''); setStopLoss('');
    }
  }, [orderModal]);

  const currentPrice = instrumentData?.price || 0;
  const spread = getSpread(currentPrice, instrument?.cat);
  const askPrice = currentPrice + spread / 2;
  const bidPrice = currentPrice - spread / 2;
  const execPrice = tab === 'market' ? (direction === 'buy' ? askPrice : bidPrice) : (parseFloat(targetPrice) || 0);
  const ls = getLotSize(instrument?.cat, instrument?.id);
  const totalValue = execPrice > 0 ? getNotionalUSD(instrument?.id || selectedId, execPrice, quantity, instrument?.cat, ls, marketData) : 0;
  const requiredMargin = totalValue / leverageMultiplier;
  const pctOfBalance = balance > 0 ? (requiredMargin / balance * 100) : 0;
  const riskColor = () => { if (pctOfBalance < 5) return 'var(--color-green)'; if (pctOfBalance < 15) return 'var(--color-yellow)'; if (pctOfBalance < 30) return 'var(--color-text)'; return 'var(--color-red)'; };
  const riskLabel = () => { if (pctOfBalance < 5) return 'Low'; if (pctOfBalance < 15) return 'Medium'; if (pctOfBalance < 30) return 'High'; return 'Critical'; };

  const applyQty = (raw) => { setQtyRaw(raw); let v = parseFloat(raw); if (isNaN(v) || v < 0.01) return; if (v > 100) v = 100; setQuantity(parseFloat(v.toFixed(2))); };
  const blurQty = () => { let v = parseFloat(qtyRaw); if (isNaN(v) || v < 0.01) v = 0.01; if (v > 100) v = 100; v = parseFloat(v.toFixed(2)); setQuantity(v); setQtyRaw(String(v)); };
  const stepQty = (delta) => { let v = quantity + delta; if (v < 0.01) v = 0.01; if (v > 100) v = 100; v = parseFloat(v.toFixed(2)); setQuantity(v); setQtyRaw(String(v)); };

  const handleSubmit = async () => {
    if (!instrument || isSubmitting || !dataReady) return;
    if (!user?.questionnaireComplete) {
      dispatch(showToast({ message: 'Please complete the trading questionnaire before placing trades', type: 'error', duration: 5000 }));
      window.dispatchEvent(new CustomEvent('open-questionnaire'));
      return;
    }
    if (tab === 'pending') { const tp = parseFloat(targetPrice); if (!targetPrice || isNaN(tp) || tp <= 0) { dispatch(showToast({ message: 'Please enter a valid target price', type: 'error' })); return; } }
    else { if (requiredMargin > balance) { dispatch(showToast({ message: `Insufficient margin. Need $${requiredMargin.toFixed(2)} · Leverage ${leverageDisplay}`, type: 'error' })); return; } }
    setIsSubmitting(true);
    try {
      if (tab === 'market') {
        const endpoint = direction === 'buy' ? '/trade/buy' : '/trade/sell';
        const payload = { instrumentId: instrument.id, category: instrument.cat, quantity, orderType: 'market' };
        const res = await api.post(endpoint, payload);
        if (res.data.success) {
          dispatch(setBalance(res.data.newBalance));
          if (res.data.position) dispatch(updatePosition({ instrumentId: instrument.id, position: res.data.position }));
          dispatch(showToast({ message: `${direction.toUpperCase()} ${quantity} lot(s) ${instrument.id}`, type: direction === 'buy' ? 'buy' : 'sell' }));
          dispatch(closeModal('orderModal'));
        }
      } else {
        const payload = { instrumentId: instrument.id, category: instrument.cat, direction, orderType: pendingType, quantity, targetPrice: parseFloat(targetPrice) };
        const res = await api.post('/trade/pending', payload);
        if (res.data.success) { dispatch(addPendingOrder(res.data.order)); dispatch(showToast({ message: `${direction.toUpperCase()} ${pendingType.toUpperCase()} placed: ${quantity} lot(s) ${instrument.id} @ ${fmtP(parseFloat(targetPrice), instrument.cat)}`, type: 'order' })); dispatch(closeModal('orderModal')); }
      }
    } catch (err) { dispatch(showToast({ message: err.response?.data?.message || 'Failed to place order', type: 'error' })); }
    finally { setIsSubmitting(false); }
  };

  const btnColor = direction === 'buy' ? 'var(--color-gradientGreen)' : 'var(--color-gradientRed)';
  const btnLabel = tab === 'market' ? `${direction === 'buy' ? 'BUY' : 'SELL'} MARKET` : `Place ${direction === 'buy' ? 'BUY' : 'SELL'} ${pendingType.toUpperCase()}`;

  const inputStyle = { background: 'var(--color-bgInput)', border: '1px solid var(--color-borderMedium)', color: 'var(--color-text)' };

  return (
    <>
      <style>{`.custom-scrollbar::-webkit-scrollbar{width:14px;height:14px}.custom-scrollbar::-webkit-scrollbar-track{background:var(--color-bgSurfaceLight);border-radius:12px;margin:8px 2px;border:1px solid var(--color-borderMedium)}.custom-scrollbar::-webkit-scrollbar-thumb{background:linear-gradient(180deg,var(--color-orange),var(--color-orangeDark));border-radius:12px;border:3px solid var(--color-bgSurfaceLight);min-height:50px}.custom-scrollbar{scrollbar-width:auto;scrollbar-color:var(--color-orange) var(--color-bgSurfaceLight)}`}</style>
      <div className={`fixed inset-0 z-[800] flex items-center justify-center p-4 transition-all duration-300 ${orderModal ? 'visible' : 'invisible'}`}>
        <div className={`absolute inset-0 backdrop-blur-md transition-opacity duration-300 ${orderModal ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: 'var(--color-bgOverlay)' }} onClick={() => dispatch(closeModal('orderModal'))} />
        <div className={`relative rounded-2xl w-full max-w-[560px] max-h-[92vh] overflow-y-auto shadow-2xl transition-all duration-300 custom-scrollbar ${orderModal ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
          style={{ background: 'var(--color-bgSurface)', border: '1px solid var(--color-border)' }}>
          
          {/* Header */}
          <div className="sticky top-0 z-10 px-5 py-4 flex items-center justify-between"
            style={{ background: 'var(--color-bgSurface)', borderBottom: '1px solid var(--color-borderMedium)' }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center text-base"
                style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)', color: 'var(--color-text)' }}>
                <i className="fas fa-file-invoice" />
              </div>
              <div>
                <div className="font-syne text-base font-extrabold" style={{ color: 'var(--color-text)' }}>New Order</div>
                <div className="text-[10px] font-mono" style={{ color: 'var(--color-textSecondary)' }}>Leverage: {leverageDisplay} · Margin rate: {marginRatePct}%</div>
              </div>
            </div>
            <button onClick={() => dispatch(closeModal('orderModal'))} className="w-7 h-7 rounded-md cursor-pointer text-sm flex items-center justify-center transition-all"
              style={{ background: 'var(--color-bgCard)', border: '1px solid var(--color-borderMedium)', color: 'var(--color-textSecondary)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-red)'; e.currentTarget.style.color = 'var(--color-red)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-borderMedium)'; e.currentTarget.style.color = 'var(--color-textSecondary)'; }}>
              <i className="fas fa-xmark" />
            </button>
          </div>

          {/* Tab Selector */}
          <div className="grid grid-cols-2 mx-5 mt-4 rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-borderStrong)' }}>
            {[{ key: 'market', label: 'New Market Order', icon: 'fa-bolt' }, { key: 'pending', label: 'New Pending Order', icon: 'fa-clock' }].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} className="py-2.5 text-[10px] font-syne font-bold tracking-wide flex items-center justify-center gap-1.5 transition-all"
                style={{
                  background: tab === t.key ? 'var(--color-orangeBg)' : 'var(--color-bgCard)',
                  color: tab === t.key ? 'var(--color-text)' : 'var(--color-textSecondary)',
                  borderBottom: tab === t.key ? '2px solid var(--color-orange)' : '2px solid transparent'
                }}>
                <i className={`fas ${t.icon} text-[9px]`} />{t.label}
              </button>
            ))}
          </div>

          {/* Symbol Select */}
          <div className="px-5 pt-4 pb-2">
            <div className="text-[8px] font-mono tracking-[0.1em] uppercase mb-2 flex items-center gap-1" style={{ color: 'var(--color-textSecondary)' }}>
              <i className="fas fa-coins text-[8px]" style={{ color: 'var(--color-text)' }} />Symbol
            </div>
            <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
              className="w-full rounded-xl px-4 py-2.5 font-mono text-sm appearance-none cursor-pointer outline-none transition-all"
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = 'var(--color-orange)'}
              onBlur={e => e.target.style.borderColor = 'var(--color-borderMedium)'}>
              {INSTRUMENTS.map(inst => (<option key={inst.id} value={inst.id} style={{ background: 'var(--color-bgElevated)', color: 'var(--color-text)' }}>{inst.id} — {inst.name}</option>))}
            </select>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-2 px-5 pb-3">
            {[
              { label: 'BID', value: dataReady ? fmtP(bidPrice, instrument?.cat) : '—', color: 'var(--color-red)' },
              { label: 'ASK', value: dataReady ? fmtP(askPrice, instrument?.cat) : '—', color: 'var(--color-green)' },
              { label: 'BALANCE', value: `$${parseFloat(balance || 0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}`, color: 'var(--color-text)' },
              { label: 'CHG%', value: dataReady ? `${instrumentData.changePct >= 0 ? '+' : ''}${instrumentData.changePct?.toFixed(2)}%` : '—', color: (instrumentData?.changePct ?? 0) >= 0 ? 'var(--color-green)' : 'var(--color-red)' }
            ].map(item => (
              <div key={item.label} className="rounded-xl p-2 text-center" style={{ background: 'var(--color-bgCard)', border: '1px solid var(--color-borderMedium)' }}>
                <div className="text-[7px] font-mono mb-1" style={{ color: 'var(--color-textSecondary)' }}>{item.label}</div>
                <div className="font-syne text-[11px] font-extrabold" style={{ color: item.color }}>{item.value}</div>
              </div>
            ))}
          </div>

          {/* Direction */}
          <div className="px-5 pb-3 pt-3" style={{ borderTop: '1px solid var(--color-borderMedium)' }}>
            <div className="text-[8px] font-mono tracking-[0.1em] uppercase mb-2 flex items-center gap-1" style={{ color: 'var(--color-textSecondary)' }}>
              <i className="fas fa-arrows-up-down text-[8px]" style={{ color: 'var(--color-text)' }} />Direction
            </div>
            <div className="grid grid-cols-2 gap-2">
              {['buy', 'sell'].map(dir => (
                <button key={dir} onClick={() => setDirection(dir)} className="py-2.5 rounded-xl font-syne font-bold text-xs transition-all border flex items-center justify-center gap-1.5"
                  style={{
                    background: direction === dir ? (dir === 'buy' ? 'var(--color-greenBg)' : 'var(--color-redBg)') : 'var(--color-bgCard)',
                    borderColor: direction === dir ? (dir === 'buy' ? 'var(--color-greenBorder)' : 'var(--color-redBorder)') : 'var(--color-borderMedium)',
                    color: direction === dir ? (dir === 'buy' ? 'var(--color-green)' : 'var(--color-red)') : 'var(--color-textSecondary)'
                  }}>
                  <i className={`fas fa-arrow-trend-${dir === 'buy' ? 'up' : 'down'} text-[10px]`} />{dir === 'buy' ? 'BUY / LONG' : 'SELL / SHORT'}
                </button>
              ))}
            </div>
          </div>

          {/* Pending Type */}
          {tab === 'pending' && (
            <div className="px-5 pb-3">
              <div className="text-[8px] font-mono tracking-[0.1em] uppercase mb-2 flex items-center gap-1" style={{ color: 'var(--color-textSecondary)' }}>
                <i className="fas fa-list-check text-[8px]" style={{ color: 'var(--color-text)' }} />Type
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[{ key: 'limit', label: 'LIMIT', desc: direction === 'buy' ? 'Buy when price drops' : 'Sell when price rises' }, { key: 'stop', label: 'STOP', desc: direction === 'buy' ? 'Buy on breakout above' : 'Sell on breakdown below' }].map(opt => (
                  <button key={opt.key} onClick={() => setPendingType(opt.key)} className="py-2 rounded-xl text-center transition-all border"
                    style={{
                      background: pendingType === opt.key ? 'var(--color-orangeBg)' : 'var(--color-bgCard)',
                      borderColor: pendingType === opt.key ? 'var(--color-orangeBorder)' : 'var(--color-borderMedium)',
                      color: pendingType === opt.key ? 'var(--color-text)' : 'var(--color-textSecondary)'
                    }}>
                    <div className="text-[9px] font-syne font-bold">{direction === 'buy' ? 'BUY' : 'SELL'} {opt.label}</div>
                    <div className="text-[7px] mt-0.5" style={{ color: 'var(--color-textSecondary)' }}>{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Parameters */}
          <div className="px-5 pb-3 pt-3" style={{ borderTop: '1px solid var(--color-borderMedium)' }}>
            <div className="text-[8px] font-mono tracking-[0.1em] uppercase mb-3 flex items-center gap-1" style={{ color: 'var(--color-textSecondary)' }}>
              <i className="fas fa-sliders text-[8px]" style={{ color: 'var(--color-text)' }} />Parameters
            </div>
            <div className={`grid gap-3 mb-3 ${tab === 'pending' ? 'grid-cols-2' : 'grid-cols-1'}`}>
              <div>
                <div className="text-[8px] font-mono mb-1.5 flex items-center gap-1" style={{ color: 'var(--color-textSecondary)' }}>
                  <i className="fas fa-layer-group text-[7px]" style={{ color: 'var(--color-text)' }} />Volume (Lots)
                </div>
                <div className="flex items-center rounded-lg overflow-hidden" style={{ border: '1px solid var(--color-borderMedium)', background: 'var(--color-bgInput)' }}>
                  <button onClick={() => stepQty(-0.01)} className="w-8 h-9 cursor-pointer text-base flex items-center justify-center transition-all"
                    style={{ background: 'var(--color-bgSurfaceLight)', borderRight: '1px solid var(--color-borderMedium)', color: 'var(--color-text)' }}>−</button>
                  <input type="text" inputMode="decimal" value={qtyRaw} onChange={e => applyQty(e.target.value)} onBlur={blurQty}
                    className="flex-1 bg-transparent text-center font-mono text-sm py-2 outline-none" style={{ color: 'var(--color-text)' }} />
                  <button onClick={() => stepQty(0.01)} className="w-8 h-9 cursor-pointer text-base flex items-center justify-center transition-all"
                    style={{ background: 'var(--color-bgSurfaceLight)', borderLeft: '1px solid var(--color-borderMedium)', color: 'var(--color-text)' }}>+</button>
                </div>
              </div>
              {tab === 'pending' && (
                <div>
                  <div className="text-[8px] font-mono mb-1.5 flex items-center gap-1" style={{ color: 'var(--color-textSecondary)' }}>
                    <i className="fas fa-tag text-[7px]" style={{ color: 'var(--color-text)' }} />At Price
                  </div>
                  <input type="text" inputMode="decimal" value={targetPrice} onChange={e => setTargetPrice(e.target.value)}
                    className="w-full rounded-lg px-3 py-2.5 font-mono text-sm outline-none transition-all" style={inputStyle}
                    onFocus={e => e.target.style.borderColor = 'var(--color-orange)'}
                    onBlur={e => e.target.style.borderColor = 'var(--color-borderMedium)'} />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[8px] font-mono mb-1.5 flex items-center gap-1" style={{ color: 'var(--color-green)' }}>
                  <i className="fas fa-bullseye text-[7px]" />Take Profit
                </div>
                <input type="text" inputMode="decimal" value={takeProfit} onChange={e => setTakeProfit(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 font-mono text-sm outline-none transition-all" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--color-green)'}
                  onBlur={e => e.target.style.borderColor = 'var(--color-borderMedium)'} />
              </div>
              <div>
                <div className="text-[8px] font-mono mb-1.5 flex items-center gap-1" style={{ color: 'var(--color-red)' }}>
                  <i className="fas fa-shield-halved text-[7px]" />Stop Loss
                </div>
                <input type="text" inputMode="decimal" value={stopLoss} onChange={e => setStopLoss(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 font-mono text-sm outline-none transition-all" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--color-red)'}
                  onBlur={e => e.target.style.borderColor = 'var(--color-borderMedium)'} />
              </div>
            </div>
          </div>

          {/* Order Estimation */}
          <div className="mx-5 mb-4 rounded-xl overflow-hidden" style={{ border: '1px solid var(--color-borderMedium)' }}>
            <div className="flex items-center justify-between px-3 py-2 border-b" style={{ background: 'var(--color-bgCard)', borderColor: 'var(--color-borderMedium)' }}>
              <span className="text-[9px] font-syne font-bold flex items-center gap-1.5" style={{ color: 'var(--color-text)' }}><i className="fas fa-calculator text-[8px]" />{tab === 'market' ? 'Order Estimation' : 'Pending Order Summary'}</span>
              <div className="flex items-center gap-1.5">
                <span className="text-[7px] px-1.5 py-0.5 rounded font-mono font-bold"
                  style={{ background: direction === 'buy' ? 'var(--color-greenBg)' : 'var(--color-redBg)', color: direction === 'buy' ? 'var(--color-green)' : 'var(--color-red)', border: `1px solid ${direction === 'buy' ? 'var(--color-greenBorder)' : 'var(--color-redBorder)'}` }}>{direction.toUpperCase()}</span>
                <span className="text-[7px] px-1.5 py-0.5 rounded font-mono font-bold"
                  style={{ background: 'var(--color-orangeBg)', color: 'var(--color-text)', border: '1px solid var(--color-orangeBorder)' }}>{tab === 'market' ? 'MARKET' : pendingType.toUpperCase()}</span>
              </div>
            </div>
            <div className="divide-y" style={{ background: 'var(--color-bgCard)', borderColor: 'var(--color-borderLight)' }}>
              {[
                { label: 'Total Position Value', value: execPrice > 0 ? `$${totalValue.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}` : '—', color: 'var(--color-textSecondary)' },
                { label: `Required Margin (${leverageDisplay} · ${marginRatePct}%)`, value: execPrice > 0 ? `$${requiredMargin.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}` : '—', color: 'var(--color-text)', highlight: true },
                { label: '% of Balance', value: execPrice > 0 ? `${pctOfBalance.toFixed(2)}% · ${riskLabel()}` : '—', color: riskColor() },
              ].map((row, i) => (
                <div key={i} className="flex justify-between items-center px-3 py-2" style={{ background: row.highlight ? 'var(--color-orangeBg)' : 'transparent' }}>
                  <span className="text-[9px] font-bold" style={{ color: row.color }}>{row.label}</span>
                  <span className="font-mono text-[11px] font-bold" style={{ color: row.color }}>{row.value}</span>
                </div>
              ))}
            </div>
            <div className="px-3 py-2" style={{ borderTop: '1px solid var(--color-borderMedium)', background: 'var(--color-bgCard)' }}>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--color-borderMedium)' }}>
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(pctOfBalance * 2, 100)}%`, background: riskColor() }} />
              </div>
            </div>
          </div>

          {/* Insufficient Margin */}
          {tab === 'market' && dataReady && requiredMargin > balance && (
            <div className="mx-5 mb-3 p-3 rounded-xl flex gap-2.5"
              style={{ background: 'var(--color-redBg)', border: '1px solid var(--color-redBorder)' }}>
              <i className="fas fa-triangle-exclamation text-sm mt-0.5" style={{ color: 'var(--color-red)' }} />
              <div className="text-[9px] font-mono leading-relaxed" style={{ color: 'var(--color-redLight)' }}>Insufficient margin. Need <b>${requiredMargin.toFixed(2)}</b> but balance is <b>${parseFloat(balance).toFixed(2)}</b>.</div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 p-5 pt-0" style={{ borderTop: '1px solid var(--color-borderMedium)', background: 'var(--color-bgCard)' }}>
            <button onClick={() => dispatch(closeModal('orderModal'))} className="flex-1 py-3 rounded-full font-syne font-bold text-xs transition-all"
              style={{ background: 'var(--color-bgCard)', border: '1px solid var(--color-borderHover)', color: 'var(--color-textSecondary)' }}>
              <i className="fas fa-xmark mr-1" />Cancel
            </button>
            <button onClick={handleSubmit} disabled={isSubmitting || !dataReady || (tab === 'market' && requiredMargin > balance)}
              className="flex-2 px-8 py-3 rounded-full font-syne font-bold text-xs transition-all active:translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: btnColor, color: '#ffffff' }}>
              {isSubmitting ? <><span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5" />Processing…</>
                : !dataReady ? <><i className="fas fa-spinner fa-spin mr-1" />Loading prices…</>
                : <><i className={`fas fa-arrow-trend-${direction === 'buy' ? 'up' : 'down'} mr-1.5`} />{btnLabel}</>}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderModal;